import { Box, Container } from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import React, { FC } from 'react';

interface IProps {
  backdropSrc: string;
  maxHeight?: string | number;
  title?: JSX.Element;
  subTitle?: JSX.Element;
  children?: React.ReactNode;
  id?: string;
  style?: any;
}

const useStyles = (theme: Theme, maxHeight: string | number) => ({
  root: {
    position: 'relative !important',
    display: 'flex !important',
    width: '100% !important',
    height: `${maxHeight} !important`,
    overflow: 'hidden !important',
  },
  backdrop: {
    width: '100% !important',
    objectFit: 'cover !important',
    objectPosition: 'bottom center',
  },
  containerRoot: {
    display: 'flex !important',
    flexDirection: 'column !important',
    justifyContent: 'center !important',
    alignItems: 'center !important',
    position: 'absolute !important',
    top: '50% !important',
    left: '50% !important',
    transform: 'translate(-50%, -50%) !important',
    maxHeight: `calc(${maxHeight} - ${theme.spacing(4)}) !important`,
    paddingTop: `${theme.spacing(2)} !important`,
    paddingBottom: `${theme.spacing(2)} !important`,
  },
  title: {
    display: 'flex !important',
    flex: '1 1 100% !important',
    maxWidth: `${theme.breakpoints.values.sm} !important`,
    marginBottom: `${theme.spacing(2)} !important`,
    '& > img': {
      width: 'auto !important',
      maxWidth: '100% !important',
      height: '100% !important',
      maxHeight: `calc(${maxHeight} / 2) !important`,
    },
  },
  subTitle: {
    marginBottom: `${theme.spacing(2)} !important`,
  },
});

/**
 *
 * Renders a responsive container with specified image backdrop, maxHeight can be configured through maxHeight prop.
 *
 * @params backdropSrc required string, maxHeight optional string | number, title optional JSX.Element, subTitle optional JSX.Element, children optionsl ReactNode
 * @returns JSX.Element
 * @example
 * <SiteHeader
 *   backdropSrc={'path/to/image'}
 *   maxHeight={400}
 *   title={<Typography>Some title</Typography>}
 *   subTitle={<Typography>Some sub title</Typography>}
 * >
 *   {children}
 * </SiteHeader>
 */
export const SiteHeader: FC<IProps> = ({
  backdropSrc,
  maxHeight = '50vh',
  title,
  subTitle,
  children,
  id,
  style,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme, maxHeight);

  return (
    <Box sx={classes.root} id={id} style={style}>
      {backdropSrc && <Box component="img" sx={classes.backdrop} src={backdropSrc} />}
      <Container sx={classes.containerRoot} maxWidth="lg">
        {title && window.SETTINGS && (window.SETTINGS as any).TITLE_SHOW && (
          <Box sx={classes.title}>{title}</Box>
        )}
        {subTitle && <Box sx={classes.subTitle}>{subTitle}</Box>}
        {children && <div>{children}</div>}
      </Container>
    </Box>
  );
};
